/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, ContextualHelp, type ContextualHelpProps, Dialog, breakpoints } from '@coursera/cds-core';

import _t from 'i18n!nls/program-loggedin-shared';

const styles = {
  fullWidthMobileButtons: css`
    ${breakpoints.only('xs')} {
      width: 100%;
      max-width: none;
    }
  `,
};

export interface ContextualHelpDialogProps {
  ariaLabel: string;
  /** The text that initially displays along with the (i) icon */
  label: ContextualHelpProps['label'];
  /** The heading of the opened dialog */
  dialogHeading: React.ReactText;
}

/**
 * A standardized implementation of [CDS' Contextual Help](https://www.coursera.org/cds-storybook/latest/index.html?path=/docs/components-surfaces-contextual-help--default)
 * with the following restrictions:
 *
 * 1. It will always render a dialog
 * 2. In the dialog, there will always be exactly one action: "OK". It will close the dialog.
 * 3. The dialog will have a header and a body
 * 4. The "OK" button will render at full width on mobile
 *
 * The component's children will be rendered in the body of the dialog.
 */
const ContextualHelpDialog = ({
  ariaLabel,
  label,
  dialogHeading,
  children,
  ...otherProps
}: React.PropsWithChildren<ContextualHelpDialogProps>) => {
  /** Generate a random ID on component mount */
  const randomId = React.useMemo(() => Math.random().toString(36).substr(2), []);
  return (
    <ContextualHelp
      label={label}
      helpToggleProps={{
        'aria-label': ariaLabel,
      }}
      {...otherProps}
    >
      {({ onClose, isExpanded = false }) => (
        <Dialog aria-describedby={randomId} open={isExpanded} onClose={onClose}>
          <Dialog.HeadingGroup id={randomId}>{dialogHeading}</Dialog.HeadingGroup>

          <Dialog.Content>{children}</Dialog.Content>

          <Dialog.Actions>
            <Button css={styles.fullWidthMobileButtons} type="button" size="medium" variant="primary" onClick={onClose}>
              {_t('OK')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      )}
    </ContextualHelp>
  );
};

export default ContextualHelpDialog;
